// V2 port of v1's MaterialAwareStartButton.
//
// Lazy-checks per-step material availability (via IntersectionObserver) and
// renders one of: START / CAN MAKE N / SHORTAGE / BLOCKED, with colours that
// match v1 (success / warning / error / grey). Click forwards to onStart(step).

import React, { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { PlayArrow, Block, Lock } from '@mui/icons-material';
import { v2 } from '../../../../services/api';

export default function MaterialAwareStartButtonV2({ step, onStart, testId }) {
  const [status, setStatus] = useState('loading');
  const [maxProducible, setMaxProducible] = useState(0);
  const [shortNames, setShortNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  const total = parseFloat(step?.quantity_to_produce) || 0;
  const done = parseFloat(step?.quantity_completed) || 0;
  const remaining = Math.max(0, total - done);

  // Multi-step dependency block — same semantics as v1.
  const isBlockedByDependency =
    step?.total_steps > 1 && step?.depends_on_id != null && !step?.can_start;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return undefined;
    if (isBlockedByDependency) {
      setStatus('blocked');
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    async function check() {
      setLoading(true);
      try {
        const res = await v2.workOrders.materialAvailability(
          step.work_order_id,
          { quantity: remaining || 1, step: step.step_id },
        );
        if (cancelled) return;
        const reqs = res.data?.shortages || [];
        // What's constraining production — surfaced in the tooltip so the
        // operator doesn't have to open the row to find the short material.
        setShortNames(reqs.map((r) => r.item_name || r.item_code));
        // parseFloat, not parseInt: older backends stringify Decimal('40')
        // as '4E+1' and parseInt stops at the 'E' → 4. parseFloat handles
        // exponent notation; floor keeps whole units.
        let max = Math.floor(parseFloat(res.data?.max_producible ?? 0));
        if (Number.isNaN(max)) max = 0;
        // Multi-step cap — mirrors v1 (ShopFloorTerminal.js line ~993):
        // a downstream step can never make more than its predecessor has
        // released, regardless of what materials are in stock. The backend's
        // max_producible only knows about leaf material on hand, not about
        // upstream-step output, so we have to cap on the client.
        if (
          step.total_steps > 1
          && step.depends_on_id != null
          && step.qty_available_from_previous_step != null
        ) {
          const availFromPrev = parseFloat(step.qty_available_from_previous_step);
          if (!Number.isNaN(availFromPrev)) {
            max = Math.max(0, Math.min(max, Math.floor(availFromPrev)));
          }
        }
        setMaxProducible(max);
        if (max <= 0) {
          setStatus('none');
        } else if (reqs.length === 0 && max >= remaining) {
          setStatus('full');
        } else if (max >= remaining) {
          setStatus('full');
        } else {
          setStatus('partial');
        }
      } catch (err) {
        console.error('MaterialAwareStartButtonV2: failed', err);
        if (!cancelled) setStatus('unknown');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    check();
    return () => { cancelled = true; };
  }, [
    isVisible,
    isBlockedByDependency,
    step?.work_order_id,
    step?.step_id,
    step?.quantity_completed,
    step?.qty_available_from_previous_step,
    step?.total_steps,
    step?.depends_on_id,
    remaining,
  ]);

  const buttonColor = (() => {
    if (loading) return 'grey.500';
    if (isBlockedByDependency) return 'grey.600';
    switch (status) {
      case 'full': return 'success.main';
      case 'partial': return 'warning.main';
      case 'none': return 'error.main';
      case 'blocked': return 'grey.600';
      default: return 'grey.500';
    }
  })();

  const buttonText = (() => {
    if (loading) return 'Checking...';
    if (done >= total) return 'Complete';
    if (isBlockedByDependency) return `CAN MAKE 0 OF ${remaining}`;
    // "x OF y": x capped at what's actually asked for, y = remaining need.
    switch (status) {
      case 'full': return `CAN MAKE ${Math.min(maxProducible, remaining)} OF ${remaining}`;
      case 'partial': return `CAN MAKE ${maxProducible} OF ${remaining}`;
      case 'none': return 'SHORTAGE';
      case 'blocked': return 'BLOCKED';
      default: return 'START';
    }
  })();

  const tooltipText = (() => {
    if (loading) return 'Checking material availability...';
    if (isBlockedByDependency) {
      return `Step ${(step?.sequence || 1) - 1} hasn't started yet — click to override`;
    }
    const shortSuffix = shortNames.length
      ? ` — short of: ${shortNames.slice(0, 3).join(', ')}${shortNames.length > 3 ? '…' : ''}`
      : '';
    switch (status) {
      case 'full':
        return `All materials available — can make ${maxProducible} (${remaining} needed)`;
      case 'partial':
        return `Can make ${maxProducible} of ${remaining} needed${shortSuffix}`;
      case 'none':
        return `No materials available — production blocked${shortSuffix}`;
      default:
        return 'Material status unknown';
    }
  })();

  const isDisabled = loading || done >= total || status === 'none';

  return (
    <Tooltip title={tooltipText} arrow>
      <span ref={ref}>
        <Button
          variant="contained"
          size="large"
          data-testid={testId || `btn-start-${step?.step_id}`}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> :
            isBlockedByDependency ? <Lock /> :
            status === 'none' ? <Block /> :
            <PlayArrow />
          }
          onClick={(e) => {
            e.stopPropagation();
            onStart(step);
          }}
          disabled={isDisabled}
          sx={{
            minWidth: 140,
            backgroundColor: buttonColor,
            '&:hover': {
              backgroundColor: isBlockedByDependency ? 'grey.700' :
                status === 'full' ? 'success.dark' :
                status === 'partial' ? 'warning.dark' :
                status === 'none' ? 'error.dark' : 'grey.600',
            },
            '&.Mui-disabled': {
              backgroundColor: isBlockedByDependency ? 'grey.500' :
                status === 'none' ? 'error.light' : 'grey.400',
              color: 'white',
            },
          }}
        >
          {buttonText}
        </Button>
      </span>
    </Tooltip>
  );
}
